import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Filter, ArrowUpAZ, ArrowDownAZ } from 'lucide-react';
import { reportingApi } from '../../../services/api';

const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

/**
 * Report parameters rendered as a filterable datasheet table.
 * Column headers match FilterableDataGrid style — clickable to open filter dropdown.
 * Selected values are listed as rows in the table body below each column header.
 */
const ReportParameterTable = ({ params, values, onChange, reportId, paramOptions = {}, sortLookup = {} }) => {
  const [openField, setOpenField]     = useState(null);
  const [fieldOptions, setFieldOptions] = useState(() =>
    Object.fromEntries(params.map(p => [p.field, paramOptions[p.field] || []]))
  );
  const [loading, setLoading]         = useState(false);
  const [pending, setPending]         = useState(new Set());
  const [search, setSearch]           = useState('');
  const [sortDir, setSortDir]         = useState(null); // null | 'asc' | 'desc'
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const headerRefs = useRef({});
  const dropdownRef = useRef(null);
  const searchRef   = useRef(null);

  // Build cascading filter payload for a given field
  const buildFilterObj = useCallback((field) => {
    const relevant = {};
    Object.entries(values).forEach(([f, sels]) => {
      if (f === field) return;
      const picked = (sels || []).filter(s => s.id !== '__all__');
      if (picked.length === 0) return;
      relevant[toCamelCase(f)] = picked.map(s => ({ operator: s.operator, value: s.value }));
    });
    return Object.keys(relevant).length > 0 ? relevant : null;
  }, [values]);

  const openDropdown = (param) => {
    const th = headerRefs.current[param.field];
    if (th) {
      const rect = th.getBoundingClientRect();
      const H = 380, W = 280;
      let top  = rect.bottom + 2;
      let left = rect.left;
      if (top  + H > window.innerHeight) top  = rect.top - H - 2;
      if (left + W > window.innerWidth)  left = window.innerWidth - W - 8;
      setDropdownPos({ top, left });
    }
    setSearch('');
    setSortDir(null);
    setLoading(true);
    setOpenField(param.field);

    reportingApi.getParameterOptions(reportId, param.field, buildFilterObj(param.field))
      .then(res => {
        const opts = res.data || [];
        setFieldOptions(prev => ({ ...prev, [param.field]: opts }));

        const sels = values[param.field] || [];
        const isAll = sels.some(s => s.id === '__all__') || sels.length === 0;
        if (isAll) {
          setPending(new Set(opts.map(o => String(o.id))));
        } else {
          setPending(new Set(sels.filter(s => s.type === 'picked').map(s => String(s.id))));
        }
      })
      .catch(() => { setFieldOptions(prev => ({ ...prev, [param.field]: [] })); setPending(new Set()); })
      .finally(() => {
        setLoading(false);
        setTimeout(() => searchRef.current?.focus(), 50);
      });
  };

  const closeDropdown = () => { setOpenField(null); setSearch(''); };

  useEffect(() => {
    if (!openField) return;
    const handler = (e) => {
      if (headerRefs.current[openField]?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      closeDropdown();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openField]);

  const filteredOptions = useMemo(() => {
    const opts = openField ? (fieldOptions[openField] || []) : [];
    const filtered = search
      ? opts.filter(o => String(o.display || '').toLowerCase().includes(search.toLowerCase()))
      : opts;
    if (!sortDir) return filtered;
    return [...filtered].sort((a, b) => {
      const av = String(a.display || '').toLowerCase();
      const bv = String(b.display || '').toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [openField, fieldOptions, search, sortDir]);

  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(o => pending.has(String(o.id)));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      setPending(prev => { const n = new Set(prev); filteredOptions.forEach(o => n.delete(String(o.id))); return n; });
    } else {
      setPending(prev => { const n = new Set(prev); filteredOptions.forEach(o => n.add(String(o.id))); return n; });
    }
  };

  const handleCheckbox = (opt) => {
    const id = String(opt.id);
    setPending(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleOK = () => {
    if (!openField) return;
    const opts = fieldOptions[openField] || [];
    const allChosen = opts.length > 0 && opts.every(o => pending.has(String(o.id)));
    if (allChosen || pending.size === 0) {
      onChange(openField, [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }]);
    } else {
      onChange(openField,
        opts
          .filter(o => pending.has(String(o.id)))
          .map(o => ({ type: 'picked', id: o.id, display: o.display, fullDisplay: o.fullDisplay || null, operator: 'equals', value: String(o.id) }))
      );
    }
    closeDropdown();
  };

  // Always exactly 4 rows — overflow capped with a message on row 3
  const rowCount = 4;

  return (
    <div className="stui-data-grid-container no-column-resize" style={{ overflowX: 'auto' }}>
      <table className="stui-data-grid" style={{ width: '100%', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {params.map(param => {
              const sels      = values[param.field] || [];
              const isActive  = openField === param.field;
              const hasFilter = sels.length > 0 && !sels.some(s => s.id === '__all__');
              const sortPri   = sortLookup[param.field];
              return (
                <th
                  key={param.field}
                  ref={el => headerRefs.current[param.field] = el}
                  style={{ minWidth: '140px', padding: 0 }}
                >
                  <button
                    className={`stui-filter-sort-btn${hasFilter || isActive ? ' active' : ''}`}
                    onClick={() => openDropdown(param)}
                  >
                    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                      <Filter size={12} />
                      {sortPri && (
                        <span style={{
                          position: 'absolute', top: -4, right: -5,
                          width: 10, height: 10, borderRadius: '50%',
                          background: 'var(--color-primary)', color: '#fff',
                          fontSize: 7, fontWeight: 700, lineHeight: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          pointerEvents: 'none'
                        }}>
                          {sortPri}
                        </span>
                      )}
                    </span>
                    {param.label}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }, (_, rowIdx) => (
            <tr key={rowIdx} style={{ height: '28px' }}>
              {params.map(param => {
                const sels   = values[param.field] || [];
                const isAll  = sels.some(s => s.id === '__all__');
                const isEmpty = sels.length === 0;
                const picked = sels.filter(s => s.type === 'picked');
                const val    = picked[rowIdx];

                let cell;
                if (rowIdx === 0 && isEmpty) {
                  cell = <span style={{ color: 'var(--color-gray-400)', fontStyle: 'italic' }}>—</span>;
                } else if (rowIdx === 0 && isAll) {
                  cell = <span style={{ color: 'var(--color-gray-600)' }}>All</span>;
                } else if (rowIdx === 3 && picked.length > 4) {
                  cell = <span style={{ fontSize: '11px', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>Greater than 4 Selected</span>;
                } else if (val) {
                  cell = <span style={{ fontSize: '12px', color: 'var(--color-gray-700)' }}>{val.display}</span>;
                } else {
                  cell = null;
                }

                return <td key={param.field}>{cell}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {openField && (
        <div
          ref={dropdownRef}
          className="filter-dropdown"
          style={{ top: dropdownPos.top, left: dropdownPos.left, minWidth: '280px' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="filter-sort-section">
            <button className={`stui-filter-sort-btn${sortDir === 'asc' ? ' active' : ''}`} onClick={() => setSortDir(d => d === 'asc' ? null : 'asc')}>
              <ArrowUpAZ size={16} /><span>Sort A to Z</span>
            </button>
            <button className={`stui-filter-sort-btn${sortDir === 'desc' ? ' active' : ''}`} onClick={() => setSortDir(d => d === 'desc' ? null : 'desc')}>
              <ArrowDownAZ size={16} /><span>Sort Z to A</span>
            </button>
          </div>
          <div className="filter-divider" />
          <div className="filter-search">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-options">
            {loading ? (
              <div style={{ padding: '12px', color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)', textAlign: 'center' }}>
                Loading...
              </div>
            ) : (
              <>
                <label className="filter-option filter-option-select-all">
                  <input type="checkbox" checked={allFilteredSelected} onChange={handleSelectAll} />
                  <span>(Select All)</span>
                </label>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '8px 12px', color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)', fontStyle: 'italic' }}>
                    No matches
                  </div>
                ) : (
                  filteredOptions.map(opt => (
                    <label key={opt.id} className="filter-option">
                      <input
                        type="checkbox"
                        checked={pending.has(String(opt.id))}
                        onChange={() => handleCheckbox(opt)}
                      />
                      <span>{opt.display || '(blank)'}</span>
                    </label>
                  ))
                )}
              </>
            )}
          </div>
          <div className="filter-footer">
            <button className="filter-ok-btn" onClick={handleOK}>OK</button>
            <button className="filter-cancel-btn" onClick={closeDropdown}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportParameterTable;
